import {
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  useId,
} from 'react';

import { cx } from '@/shared/lib';
import { Icon } from '@/shared/ui/icon';

import styles from './Field.module.scss';

interface FieldFrameProps {
  label: string;
  labelHidden?: boolean | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}

/** Label, control, hint and error wired together for screen readers. */
export function FieldFrame({ label, labelHidden, error, hint, children }: FieldFrameProps) {
  const id = useId();
  const describedBy = cx(hint && `${id}-hint`, error && `${id}-error`) || undefined;
  return (
    <div className={styles.field}>
      <label className={cx(styles.label, labelHidden && 'visually-hidden')} htmlFor={id}>
        {label}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint && !error && (
        <p id={`${id}-hint`} className={styles.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  ref?: Ref<HTMLInputElement>;
}

export function TextField({ label, error, hint, className, ...input }: TextFieldProps) {
  return (
    <FieldFrame label={label} error={error} hint={hint}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          className={cx(styles.control, className)}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          {...input}
        />
      )}
    </FieldFrame>
  );
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  labelHidden?: boolean;
  error?: string | undefined;
  hint?: string | undefined;
  options: ReadonlyArray<{ value: string; label: string }>;
  placeholder?: string | undefined;
  ref?: Ref<HTMLSelectElement>;
}

function SelectControl({
  options,
  placeholder,
  className,
  ...select
}: Omit<SelectFieldProps, 'label' | 'labelHidden' | 'error' | 'hint'>): ReactElement {
  return (
    <div className={styles.selectWrapper}>
      <select className={cx(styles.control, styles.select, className)} {...select}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className={styles.chevron}>
        <Icon name="chevron-down" />
      </span>
    </div>
  );
}

export function SelectField({
  label,
  labelHidden,
  error,
  hint,
  ...select
}: SelectFieldProps): ReactElement {
  return (
    <FieldFrame label={label} labelHidden={labelHidden} error={error} hint={hint}>
      {({ id, describedBy, invalid }) => (
        <SelectControl
          id={id}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          {...select}
        />
      )}
    </FieldFrame>
  );
}
